<script lang="ts">
  import { onMount } from 'svelte';
  import Icon from '../icon/icon.svelte';

  type Particle = {
    type: 'text' | 'icon';
    animation: string;
    value: string;
    spawnTarget: string;

    colour?: string;

    class?: string;
    x?: string;
    y?: string;
    lifespan: number;
  };

  let particles: Array<Particle> = $state([]);
  let queue: Array<Particle> = [];

  export function queueParticle(particle: Particle) {
    queue.push(particle);
  }

  const loop = () => {
    if (queue.length) {
      let item = queue.shift();
      if (item) {
        createParticle(item);
      }
    }
  };

  function createParticle(queued: Particle) {
    particles.push(queued);
    // What the array holds is a proxy of `queued`, not `queued` itself, and only
    // writes through the proxy reach the page.
    const particle = particles[particles.length - 1];

    var target: HTMLElement | null = document.querySelector(
      `.full-screen.visible ${particle.spawnTarget}`,
    );
    if (!target) {
      return;
    }

    // Accurate positioning using getBoundingClientRect
    const rect = target.getBoundingClientRect();
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    // Center of the target element in page coordinates
    let x = rect.left + rect.width / 2 + scrollLeft;
    let y = rect.top + rect.height / 2 + scrollTop;

    // Add random offset within the element
    x += (Math.random() - 0.5) * (rect.width / 2);
    y += (Math.random() - 0.5) * (rect.height / 2);

    particle.x = x + 'px';
    particle.y = y + 'px';

    particle.lifespan = performance.now() + 2000;

    setTimeout(() => {
      particle.class = particle.animation;
    }, 10);
  }

  function onCreateParticle(event: Event) {
    queueParticle((event as CustomEvent).detail);
  }

  // Inline and unremovable before: a <Particles /> goes up with every board, so
  // each finished battle left one of these behind holding it.
  onMount(() => {
    const interval = setInterval(loop, 200);
    window.addEventListener('createParticle', onCreateParticle);
    return () => {
      clearInterval(interval);
      window.removeEventListener('createParticle', onCreateParticle);
    };
  });
</script>

<div class="particles">
  {#each particles as particle}
    <div
      class="particle {particle.class} {particle.colour}"
      style="left: {particle.x}; top: {particle.y}"
    >
      {#if particle.type == 'text'}
        {particle.value}
      {/if}

      {#if particle.type == 'icon'}
        <Icon name={particle.value} width="40px" height="40px" />
      {/if}
    </div>
  {/each}
</div>

<style lang="less">
  .particles {
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100vw;
    height: 100vh;
    pointer-events: none;

    .particle {
      opacity: 1;
      position: fixed;
      z-index: 99999999999999999999999;

      transition:
        opacity 1.5s ease-out,
        transform 1.5s ease-out;

      font-size: 36px;
      font-family: sans-serif;
      text-shadow:
        -1px -1px 0 #000,
        1px -1px 0 #000,
        -1px 1px 0 #000,
        1px 1px 0 #000;
      width: 40px;
      height: 40px;

      &.rise {
        transform: translateY(-50px);
        opacity: 0;
      }
      &.fall {
        transform: translateY(50px);
        opacity: 0;
      }
      &.grow {
        transform: scale(2);
        opacity: 0;
      }

      &.red {
        color: white;
        background-image: radial-gradient(
          circle at 50% 50%,
          rgba(255, 0, 0, 0.8),
          rgba(255, 0, 0, 0) 70%
        );
      }
      &.green {
        color: white;
        background-image: radial-gradient(
          circle at 50% 50%,
          rgba(0, 255, 0, 0.8),
          rgba(0, 255, 0, 0) 70%
        );
      }
    }
  }
</style>
